import React from 'react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import {
  addUniversalMeasurement,
  getAllCompanies,
  getUniversalMeasurement
} from '../../services/api/implementation/impl';
import { Layers, Plus, Ruler, Search } from 'lucide-react';

const MEASUREMENT_TYPES = [
  { value: 'sq/ft', label: 'Square feet (sq/ft)' },
  { value: 'sq/mt', label: 'Square metres (sq/mt)' }
];

const UniversalMeasurement = () => {
  const { collapsed } = useSidebar();
  const initialCompanyId =
    localStorage.getItem('companyId') || Cookies.get('companyId') || Cookies.get('company_id') || '';
  const [selectedCompanyId, setSelectedCompanyId] = React.useState(initialCompanyId);
  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [newMeasurement, setNewMeasurement] = React.useState({
    name: '',
    per_unit: '',
    measurement_type: 'sq/ft'
  });

  const { data: companies = [] } = useQuery({
    queryKey: ['allCompaniesForUniversalMeasurement'],
    queryFn: getAllCompanies
  });

  React.useEffect(() => {
    if (selectedCompanyId) {
      return;
    }
    if (companies.length > 0) {
      const firstCompanyId = String(companies[0].id);
      setSelectedCompanyId(firstCompanyId);
      localStorage.setItem('companyId', firstCompanyId);
    }
  }, [companies, selectedCompanyId]);

  const {
    data: universalMeasurements = [],
    isLoading,
    isError,
    refetch: refetchUniversalMeasurements
  } = useQuery({
    queryKey: ['universalMeasurement', selectedCompanyId],
    queryFn: () => getUniversalMeasurement(selectedCompanyId),
    enabled: Boolean(selectedCompanyId)
  });

  const selectedCompanyLabel = React.useMemo(() => {
    const c = companies.find((co) => String(co.id) === String(selectedCompanyId));
    return c?.company_name || c?.name || '';
  }, [companies, selectedCompanyId]);

  const filteredMeasurements = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return universalMeasurements;
    return universalMeasurements.filter((item) => {
      const name = (item.name || '').toLowerCase();
      const type = (item.measurement_type || '').toLowerCase();
      const idMatch = String(item.id).includes(q);
      return name.includes(q) || type.includes(q) || idMatch;
    });
  }, [universalMeasurements, searchQuery]);

  const { mutate: handleAddUniversalMeasurement, isPending: isAddingMeasurement } = useMutation({
    mutationFn: (payload) => addUniversalMeasurement(payload),
    onSuccess: async () => {
      toast.success('Universal Measurement added successfully');
      setIsAddModalOpen(false);
      setNewMeasurement({ name: '', per_unit: '', measurement_type: 'sq/ft' });
      await refetchUniversalMeasurements();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to add universal measurement');
    }
  });

  const handleCompanyChange = (event) => {
    const companyId = event.target.value;
    setSelectedCompanyId(companyId);
    if (companyId) {
      localStorage.setItem('companyId', companyId);
    }
    setSearchQuery('');
  };

  const handleMeasurementInputChange = (event) => {
    const { name, value } = event.target;
    setNewMeasurement((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOpenAddModal = () => {
    if (!selectedCompanyId) {
      toast.error('Please select a company first');
      return;
    }
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setNewMeasurement({ name: '', per_unit: '', measurement_type: 'sq/ft' });
  };

  const handleSubmitNewMeasurement = (event) => {
    event.preventDefault();

    if (!newMeasurement.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!newMeasurement.per_unit || Number(newMeasurement.per_unit) <= 0) {
      toast.error('Per unit must be greater than 0');
      return;
    }

    handleAddUniversalMeasurement({
      company_id: Number(selectedCompanyId),
      name: newMeasurement.name.trim(),
      per_unit: String(newMeasurement.per_unit),
      measurement_type: newMeasurement.measurement_type
    });
  };

  const inputClass =
    'w-full rounded-lg border border-[#D8DEE7] bg-[#F8FAFC] px-3 py-2 text-sm text-[#334155] outline-none focus:border-[#2A565E]';

  return (
    <Layout>
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header data={{ name: 'Universal Measurement' }} backArrow={false} />
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <GridWrapper width={100}>
          <div className="mt-[6rem] pb-10 px-2 sm:px-0">
            <div className="mb-5 rounded-xl border border-[#E7EBF1] bg-white p-3 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="w-full sm:max-w-[260px]">
                    <label htmlFor="company-select" className="mb-1 block text-xs font-medium text-[#64748B]">
                      Company
                    </label>
                    <select
                      id="company-select"
                      value={selectedCompanyId}
                      onChange={handleCompanyChange}
                      className={inputClass}>
                      <option value="">Select company</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.company_name || company.name || `Company ${company.id}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="w-full flex-1 sm:max-w-[280px]">
                    <label htmlFor="um-search" className="mb-1 block text-xs font-medium text-[#64748B]">
                      Search
                    </label>
                    <div className="relative">
                      <Search
                        size={16}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]"
                      />
                      <input
                        id="um-search"
                        type="search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Filter by name or type…"
                        disabled={!selectedCompanyId || isLoading}
                        className={`${inputClass} pl-9`}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex shrink-0 items-end">
                  <Button onClick={handleOpenAddModal}>
                    <span className="inline-flex items-center gap-1.5">
                      <Plus size={18} strokeWidth={2.25} className="shrink-0" aria-hidden />
                      Add unit
                    </span>
                  </Button>
                </div>
              </div>
            </div>

            {!selectedCompanyId && (
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-6 py-14 text-center">
                <Layers className="mb-3 h-10 w-10 text-[#94A3B8]" strokeWidth={1.25} />
                <p className="text-sm font-medium text-[#334155]">Select a company</p>
                <p className="mt-1 max-w-md text-sm text-[#64748B]">
                  Choose a company above to load and manage its universal measurement units.
                </p>
              </div>
            )}

            {selectedCompanyId && isLoading && (
              <div className="space-y-3 rounded-xl border border-[#E7EBF1] bg-white p-4 shadow-sm">
                <div className="h-4 w-48 animate-pulse rounded bg-[#E2E8F0]" />
                <div className="h-10 animate-pulse rounded-lg bg-[#F1F5F9]" />
                <div className="h-10 animate-pulse rounded-lg bg-[#F1F5F9]" />
                <div className="h-10 animate-pulse rounded-lg bg-[#F1F5F9]" />
              </div>
            )}

            {selectedCompanyId && isError && (
              <div className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm text-[#B91C1C]">
                Failed to load universal measurements. Try again or check your connection.
              </div>
            )}

            {selectedCompanyId && !isLoading && !isError && universalMeasurements.length === 0 && (
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-6 py-14 text-center">
                <Ruler className="mb-3 h-10 w-10 text-[#94A3B8]" strokeWidth={1.25} />
                <p className="text-sm font-medium text-[#334155]">No universal measurements yet</p>
                <p className="mt-1 max-w-md text-sm text-[#64748B]">
                  {selectedCompanyLabel
                    ? `${selectedCompanyLabel} has no custom units. Add one to use in pricing screens.`
                    : 'Add a unit to use it across rate lists and charges.'}
                </p>
                <div className="mt-5">
                  <Button onClick={handleOpenAddModal}>
                    <span className="inline-flex items-center gap-1.5">
                      <Plus size={18} strokeWidth={2.25} className="shrink-0" aria-hidden />
                      Add first unit
                    </span>
                  </Button>
                </div>
              </div>
            )}

            {selectedCompanyId &&
              !isLoading &&
              !isError &&
              universalMeasurements.length > 0 &&
              filteredMeasurements.length === 0 && (
                <div className="rounded-xl border border-[#E7EBF1] bg-white px-4 py-8 text-center text-sm text-[#64748B]">
                  No units match “{searchQuery.trim()}”. Clear search or try another term.
                </div>
              )}

            {selectedCompanyId && !isLoading && !isError && filteredMeasurements.length > 0 && (
              <>
                {/* Mobile cards */}
                <ul className="space-y-3 md:hidden">
                  {filteredMeasurements.map((item) => (
                    <li
                      key={item.id}
                      className="rounded-xl border border-[#E7EBF1] bg-white p-4 shadow-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-base font-semibold text-[#111827]">
                            {item.name || '—'}
                          </p>
                          <p className="mt-1 text-xs font-medium uppercase tracking-wide text-[#94A3B8]">
                            ID {item.id}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full bg-[#F1F5F9] px-2.5 py-1 text-xs font-medium text-[#475569]">
                          {item.measurement_type ?? '—'}
                        </span>
                      </div>
                      <div className="mt-3 border-t border-[#F1F5F9] pt-3">
                        <p className="text-xs text-[#64748B]">Per unit</p>
                        <p className="font-mono text-sm font-semibold text-[#0F172A]">
                          {item.per_unit ?? '—'}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Desktop table */}
                <div className="hidden overflow-hidden rounded-xl border border-[#E7EBF1] bg-white shadow-sm md:block">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-[#EEF2F6]">
                      <thead className="bg-[#F8FAFC]">
                        <tr>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                            ID
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                            Name
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                            Per unit
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                            Base type
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F1F5F9] bg-white">
                        {filteredMeasurements.map((item) => (
                          <tr key={item.id} className="transition-colors hover:bg-[#FAFBFC]">
                            <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-[#475569]">
                              {item.id}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-[#111827]">
                              {item.name || '—'}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-[#334155]">
                              {item.per_unit ?? '—'}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              <span className="inline-flex rounded-full bg-[#F1F5F9] px-2.5 py-1 text-xs font-medium text-[#475569]">
                                {item.measurement_type ?? '—'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </GridWrapper>
      </div>

      {isAddModalOpen && (
        <Modal name="Add universal measurement" onClose={handleCloseAddModal} width={40}>
          <p className="-mt-1 mb-4 text-sm text-[#64748B]">
            Creates a rate unit for the selected company. Use a clear label (e.g. “Saleable sq ft”).
          </p>
          <form onSubmit={handleSubmitNewMeasurement} className="space-y-4">
            <div>
              <label htmlFor="measurement-name" className="mb-1 block text-sm font-medium text-[#334155]">
                Name
              </label>
              <input
                id="measurement-name"
                type="text"
                name="name"
                value={newMeasurement.name}
                onChange={handleMeasurementInputChange}
                placeholder="e.g. Built-up area unit"
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="measurement-per-unit" className="mb-1 block text-sm font-medium text-[#334155]">
                Per unit
              </label>
              <input
                id="measurement-per-unit"
                type="number"
                name="per_unit"
                value={newMeasurement.per_unit}
                onChange={handleMeasurementInputChange}
                placeholder="Conversion factor"
                min="0"
                step="0.00000001"
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="measurement-type" className="mb-1 block text-sm font-medium text-[#334155]">
                Base measurement type
              </label>
              <select
                id="measurement-type"
                name="measurement_type"
                value={newMeasurement.measurement_type}
                onChange={handleMeasurementInputChange}
                className={inputClass}>
                {MEASUREMENT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 border-t border-[#EEF2F6] pt-4">
              <Button type="button" displayType="secondary" onClick={handleCloseAddModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={isAddingMeasurement}>
                {isAddingMeasurement ? 'Saving…' : 'Save unit'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  );
};

export default UniversalMeasurement;
